import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "./firebase";

function toLabel(str) {
  return str
    .replace(/([A-Z])/g, " $1")
    .replace(/([0-9]+)/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .replace(/ And /gi, " & ")
    .trim();
}

const opt = (value, order, extra = {}) => ({
  id: value,
  label: toLabel(value),
  enabled: true,
  order,
  ...extra,
});

const col = (hex, label, order) => ({ id: hex, label, enabled: true, order });

export const ALL_SECTION_OPTIONS = {
  hair: [
    "shortFlat","shortRound","shortCurly","shortWaved","sides",
    "theCaesar","theCaesarAndSidePart","shavedSides",
    "shaggy","shaggyMullet","frizzle",
    "straight01","straight02","straightAndStrand",
    "bob","bun","curly","curvy","miaWallace","longButNotTooLong",
    "fro","froBand","bigHair","dreads","dreads01","dreads02","frida",
    "hat","winterHat1","winterHat02","winterHat03","winterHat04",
    "hijab","turban",
  ].map((v, i) => opt(v, i)),

  eyes: [
    "default","happy","side","squint","surprised","wink",
    "hearts","eyeRoll","winkWacky","xDizzy","closed","cry",
  ].map((v, i) => opt(v, i)),

  eyebrows: [
    "default","raisedExcited","upDown","angry","flatNatural",
    "frownNatural","defaultNatural","angryNatural","upDownNatural",
    "raisedExcitedNatural","sadConcerned","sadConcernedNatural","unibrowNatural",
  ].map((v, i) => opt(v, i)),

  mouth: [
    "smile","default","serious","twinkle","eating","tongue",
    "concerned","disbelief","grimace","sad","screamOpen","vomit",
  ].map((v, i) => opt(v, i)),

  accessories: [
    opt("none",           0, { accessories: "blank",          accessoriesProbability: 0,   label: "None" }),
    opt("round",          1, { accessories: "round",          accessoriesProbability: 100, label: "Round Glasses" }),
    opt("prescription01", 2, { accessories: "prescription01", accessoriesProbability: 100, label: "Prescription 1" }),
    opt("prescription02", 3, { accessories: "prescription02", accessoriesProbability: 100, label: "Prescription 2" }),
    opt("sunglasses",     4, { accessories: "sunglasses",     accessoriesProbability: 100, label: "Sunglasses" }),
    opt("wayfarers",      5, { accessories: "wayfarers",      accessoriesProbability: 100, label: "Wayfarers" }),
    opt("kurt",           6, { accessories: "kurt",           accessoriesProbability: 100, label: "Kurt" }),
    opt("eyepatch",       7, { accessories: "eyepatch",       accessoriesProbability: 100, label: "Eyepatch" }),
  ],

  facialHair: [
    opt("fhNone",         0, { facialHair: "blank",           facialHairProbability: 0,   label: "None" }),
    opt("beardLight",     1, { facialHair: "beardLight",      facialHairProbability: 100, label: "Beard Light" }),
    opt("beardMedium",    2, { facialHair: "beardMedium",     facialHairProbability: 100, label: "Beard Medium" }),
    opt("beardMajestic",  3, { facialHair: "beardMajestic",   facialHairProbability: 100, label: "Beard Majestic" }),
    opt("moustache",      4, { facialHair: "moustacheFancy",  facialHairProbability: 100, label: "Moustache" }),
    opt("magnum",         5, { facialHair: "moustacheMagnum", facialHairProbability: 100, label: "Magnum" }),
  ],

  clothing: [
    "hoodie","shirtCrewNeck","shirtScoopNeck","shirtVNeck",
    "graphicShirt","collarAndSweater","blazerAndShirt","blazerAndSweater","overall",
  ].map((v, i) => opt(v, i)),
};

// ── Color palettes ─────────────────────────────────────────────────────────────
// Each group's options use `id` = the hex string (no #).
// clothingColors covers clothing, hat, and accessory color pickers.
export const COLOR_GROUPS = {
  hairColors: {
    label: "Hair & Beard Colors",
    options: [
      col("2c1b18", "Black",      0),
      col("4a312c", "Dark Brown", 1),
      col("724133", "Brown",      2),
      col("a55728", "Auburn",     3),
      col("d6b370", "Blonde",     4),
      col("ecdcbf", "Platinum",   5),
      col("c0c0c0", "Grey",       6),
      col("c93305", "Red",        7),
      col("f59797", "Pink",       8),
      col("929598", "Silver",     9),
      col("e8e1e1", "White",     10),
      col("65c9ff", "Blue",      11),
    ],
  },
  skinTones: {
    label: "Skin Tones",
    options: [
      col("ffdbb4", "Pale",      0),
      col("edb98a", "Light",     1),
      col("d08b5b", "Med Light", 2),
      col("ae5d29", "Medium",    3),
      col("f8d25c", "Golden",    4),
      col("fd9841", "Tan",       5),
      col("614335", "Dark",      6),
    ],
  },
  // Used for clothing, hats, and accessories
  clothingColors: {
    label: "Clothing / Hat / Accessory Colors",
    options: [
      col("54c9e8", "Blue",   0),
      col("3c4f5c", "Navy",   1),
      col("7c4dff", "Purple", 2),
      col("ff6b9d", "Pink",   3),
      col("ef5350", "Red",    4),
      col("43a047", "Green",  5),
      col("ff7043", "Orange", 6),
      col("262e33", "Black",  7),
      col("e8f5e9", "Mint",   8),
      col("ffd93d", "Yellow", 9),
      col("ffffff", "White", 10),
      col("929598", "Silver",11),
    ],
  },
  bgColors: {
    label: "Background Colors",
    options: [
      col("b6e3f4", "Sky Blue",    0),
      col("c0aede", "Lavender",    1),
      col("d1f4cc", "Mint",        2),
      col("ffd5dc", "Peach",       3),
      col("ffdfbf", "Pale Yellow", 4),
      col("ffffff", "White",       5),
      col("f5f5f5", "Light Grey",  6),
      col("e8d5f5", "Soft Purple", 7),
    ],
  },
};

export const SECTION_META = {
  hair:        { label: "Hair",        displayMode: "icons" },
  eyes:        { label: "Eyes",        displayMode: "icons" },
  eyebrows:    { label: "Eyebrows",    displayMode: "icons" },
  mouth:       { label: "Mouth",       displayMode: "icons" },
  accessories: { label: "Accessories", displayMode: "icons" },
  facialHair:  { label: "Facial Hair", displayMode: "icons" },
  clothing:    { label: "Clothing",    displayMode: "icons" },
};

export const DEFAULT_AVATAR_CONFIG = {
  displayMode: "icons",
  sections: Object.fromEntries(
    Object.entries(ALL_SECTION_OPTIONS).map(([key, options]) => [
      key,
      { label: SECTION_META[key].label, displayMode: SECTION_META[key].displayMode, enabled: true, options, defaultId: null },
    ])
  ),
  colors: COLOR_GROUPS,
};

// ── Firestore ──────────────────────────────────────────────────────────────────

function mergeWithDefaults(saved) {
  const merged = { ...DEFAULT_AVATAR_CONFIG, ...saved };

  // Merge avatar sections
  merged.sections = {};
  for (const [key, defSection] of Object.entries(DEFAULT_AVATAR_CONFIG.sections)) {
    const savedSection = saved.sections?.[key];
    if (!savedSection) { merged.sections[key] = defSection; continue; }
    const savedById = Object.fromEntries((savedSection.options ?? []).map((o) => [o.id, o]));
    const maxOrder = Math.max(-1, ...(savedSection.options ?? []).map((o) => o.order));
    const options = defSection.options.map((defOpt, idx) => {
      const s = savedById[defOpt.id];
      return s ? { ...defOpt, label: s.label ?? defOpt.label, enabled: s.enabled ?? true, order: s.order ?? defOpt.order } : { ...defOpt, order: maxOrder + 1 + idx };
    });
    merged.sections[key] = { ...defSection, ...savedSection, options };
  }

  // Merge color groups — preserve enabled/order per saved doc, add new colors from defaults
  merged.colors = {};
  for (const [key, defGroup] of Object.entries(DEFAULT_AVATAR_CONFIG.colors)) {
    const savedGroup = saved.colors?.[key];
    if (!savedGroup) { merged.colors[key] = defGroup; continue; }
    const savedById = Object.fromEntries((savedGroup.options ?? []).map((o) => [o.id, o]));
    const options = defGroup.options.map((defOpt) => {
      const s = savedById[defOpt.id];
      return s ? { ...defOpt, enabled: s.enabled ?? true, order: s.order ?? defOpt.order } : defOpt;
    });
    merged.colors[key] = { ...defGroup, ...savedGroup, options };
  }

  return merged;
}

export async function getAvatarBuilderConfig() {
  try {
    const snap = await getDoc(doc(db, "appConfig", "avatarBuilder"));
    if (!snap.exists()) return DEFAULT_AVATAR_CONFIG;
    return mergeWithDefaults(snap.data());
  } catch {
    return DEFAULT_AVATAR_CONFIG;
  }
}

export async function saveAvatarBuilderConfig(config) {
  await setDoc(doc(db, "appConfig", "avatarBuilder"), config);
}
